import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize, forkJoin } from 'rxjs';
import {
  CatalogCompatibility,
  CatalogComponent,
  CatalogProduct,
  CatalogProductStructure
} from '../configurator/configurator.models';
import { ConfiguratorApiService } from '../core/api/configurator-api.service';
import { I18nService } from '../core/i18n/i18n.service';
import { TranslatePipe } from '../core/i18n/translate.pipe';

interface ConfigOption {
  componentId: string;
  componentName: string;
  componentDescription: string | null;
  required: boolean;
  defaultSelected: boolean;
}

interface ConfigStep {
  componentTypeId: string;
  componentTypeName: string;
  quantity: number;
  orderNo: number;
  options: ConfigOption[];
}

interface SavedUserConfiguration {
  id: string;
  name: string;
  productId: string;
  productName: string;
  selections: Record<string, string[]>;
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-user-product-config-page',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './user-product-config-page.component.html',
  styleUrl: './shared-page.css'
})
export class UserProductConfigPageComponent implements OnInit {
  private readonly api = inject(ConfiguratorApiService);
  private readonly i18n = inject(I18nService);
  private readonly storageKey = 'configurator-user-saved-configurations';

  busy = false;
  error = '';
  info = '';

  products: CatalogProduct[] = [];
  components: CatalogComponent[] = [];
  productStructures: CatalogProductStructure[] = [];
  compatibilities: CatalogCompatibility[] = [];

  selectedProductId = '';
  steps: ConfigStep[] = [];
  selections: Record<string, Set<string>> = {};

  saveName = '';
  savedConfigurations: SavedUserConfiguration[] = [];
  selectedSavedConfigurationId = '';

  ngOnInit(): void {
    this.loadCatalog();
    this.loadSavedConfigurations();
  }

  loadCatalog(): void {
    this.busy = true;
    this.error = '';
    this.info = '';
    forkJoin({
      products: this.api.listPublicCatalogProducts(),
      components: this.api.listPublicComponents(),
      productStructures: this.api.listPublicProductStructures(),
      compatibilities: this.api.listPublicCompatibilities()
    }).pipe(finalize(() => this.busy = false)).subscribe({
      next: (data) => {
        this.products = data.products;
        this.components = data.components;
        this.productStructures = data.productStructures;
        this.compatibilities = data.compatibilities;
        this.buildSteps();
      },
      error: (err) => this.error = this.extractError(err)
    });
  }

  onProductChanged(): void {
    this.error = '';
    this.info = '';
    this.buildSteps();
  }

  toggleOption(step: ConfigStep, option: ConfigOption): void {
    if (option.required) {
      return;
    }
    const selected = this.selections[step.componentTypeId] ?? new Set<string>();
    const isCurrentlySelected = selected.has(option.componentId);
    if (!isCurrentlySelected && this.isOptionDisabled(step, option)) {
      return;
    }
    const requiredIds = new Set(step.options.filter((item) => item.required).map((item) => item.componentId));
    const availableOptionalSlots = Math.max(0, step.quantity - requiredIds.size);

    if (!isCurrentlySelected) {
      if (step.quantity === 1) {
        if (availableOptionalSlots < 1) {
          this.error = this.i18n.t('userConfig.error.tooManySelected', { quantity: step.quantity });
          return;
        }
        selected.clear();
        requiredIds.forEach((id) => selected.add(id));
        selected.add(option.componentId);
      } else {
        if (selected.size >= step.quantity) {
          this.error = this.i18n.t('userConfig.error.tooManySelected', { quantity: step.quantity });
          return;
        }
        selected.add(option.componentId);
      }
    } else {
      selected.delete(option.componentId);
      requiredIds.forEach((id) => selected.add(id));
    }

    this.error = '';
    this.selections[step.componentTypeId] = selected;
  }

  isOptionDisabled(step: ConfigStep, option: ConfigOption): boolean {
    if (this.busy || option.required) {
      return true;
    }

    const selected = this.selections[step.componentTypeId] ?? new Set<string>();
    if (selected.has(option.componentId)) {
      return false;
    }

    const requiredCount = step.options.filter((item) => item.required).length;
    if (step.quantity <= requiredCount) {
      return true;
    }

    if (step.quantity === 1) {
      return requiredCount > 0;
    }

    return selected.size >= step.quantity;
  }

  isSelected(step: ConfigStep, option: ConfigOption): boolean {
    return (this.selections[step.componentTypeId] ?? new Set<string>()).has(option.componentId);
  }

  selectedCount(step: ConfigStep): number {
    return (this.selections[step.componentTypeId] ?? new Set<string>()).size;
  }

  selectedOptions(step: ConfigStep): ConfigOption[] {
    const selected = this.selections[step.componentTypeId] ?? new Set<string>();
    return step.options.filter((option) => selected.has(option.componentId));
  }

  selectedProductName(): string {
    return this.products.find((item) => item.id === this.selectedProductId)?.name ?? '-';
  }

  isStepVisible(index: number): boolean {
    if (index <= 0) {
      return true;
    }
    const previous = this.steps[index - 1];
    return this.selectedCount(previous) > 0;
  }

  saveConfiguration(): void {
    if (!this.selectedProductId) {
      this.error = this.i18n.t('userConfig.error.productRequired');
      return;
    }

    const product = this.products.find((item) => item.id === this.selectedProductId);
    if (!product) {
      this.error = this.i18n.t('userConfig.error.productRequired');
      return;
    }

    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const payload: SavedUserConfiguration = {
      id,
      name: this.saveName.trim() || `${product.name} ${new Date().toLocaleString(this.i18n.language === 'de' ? 'de-DE' : 'en-US')}`,
      productId: product.id,
      productName: product.name,
      selections: this.serializeSelections(),
      createdAt: now,
      updatedAt: now
    };

    this.savedConfigurations = [payload, ...this.savedConfigurations];
    this.persistSavedConfigurations();
    this.saveName = '';
    this.selectedSavedConfigurationId = payload.id;
    this.error = '';
    this.info = this.i18n.t('userConfig.info.saved');
  }

  applySavedConfiguration(): void {
    if (!this.selectedSavedConfigurationId) {
      return;
    }
    const saved = this.savedConfigurations.find((item) => item.id === this.selectedSavedConfigurationId);
    if (!saved) {
      return;
    }

    this.selectedProductId = saved.productId;
    this.buildSteps();

    for (const step of this.steps) {
      const allowedIds = new Set(step.options.map((option) => option.componentId));
      const requiredIds = new Set(step.options.filter((option) => option.required).map((option) => option.componentId));
      const requested = saved.selections[step.componentTypeId] ?? [];
      const selected = new Set<string>();

      requiredIds.forEach((id) => selected.add(id));
      for (const componentId of requested) {
        if (selected.size >= step.quantity) {
          break;
        }
        if (allowedIds.has(componentId)) {
          selected.add(componentId);
        }
      }
      this.selections[step.componentTypeId] = selected;
    }

    this.error = '';
    this.info = this.i18n.t('userConfig.info.loaded');
  }

  private buildSteps(): void {
    this.steps = [];
    this.selections = {};

    if (!this.selectedProductId) {
      return;
    }

    const compatByComponentId = new Map<string, CatalogCompatibility>();
    this.compatibilities
      .filter((item) => item.productId === this.selectedProductId)
      .forEach((item) => compatByComponentId.set(item.componentId, item));

    const structures = this.productStructures
      .filter((item) => item.productId === this.selectedProductId)
      .sort((left, right) => left.orderNo - right.orderNo);

    this.steps = structures.map((structure) => {
      const options = this.components
        .filter((component) => component.componentTypeId === structure.componentTypeId)
        .filter((component) => compatByComponentId.has(component.id))
        .map((component) => {
          const compatibility = compatByComponentId.get(component.id)!;
          return {
            componentId: component.id,
            componentName: component.name,
            componentDescription: component.description,
            required: compatibility.required,
            defaultSelected: compatibility.defaultSelected
          };
        });

      const orderedDefaults = options
        .filter((option) => option.required || option.defaultSelected)
        .sort((left, right) => Number(right.required) - Number(left.required));
      const selected = new Set<string>();
      for (const option of orderedDefaults) {
        if (selected.size >= structure.quantity) {
          break;
        }
        selected.add(option.componentId);
      }
      this.selections[structure.componentTypeId] = selected;

      return {
        componentTypeId: structure.componentTypeId,
        componentTypeName: structure.componentTypeName ?? structure.componentTypeId,
        quantity: structure.quantity,
        orderNo: structure.orderNo,
        options
      };
    });
  }

  private serializeSelections(): Record<string, string[]> {
    return Object.entries(this.selections)
      .reduce<Record<string, string[]>>((accumulator, [componentTypeId, selected]) => {
        accumulator[componentTypeId] = [...selected];
        return accumulator;
      }, {});
  }

  private loadSavedConfigurations(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      return;
    }
    try {
      const parsed = JSON.parse(raw) as SavedUserConfiguration[];
      this.savedConfigurations = Array.isArray(parsed) ? parsed : [];
    } catch {
      this.savedConfigurations = [];
    }
  }

  private persistSavedConfigurations(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(this.storageKey, JSON.stringify(this.savedConfigurations));
  }

  private extractError(err: unknown): string {
    if (typeof err === 'object' && err !== null && 'error' in err) {
      const payload = (err as { error?: { error?: string } }).error;
      if (payload?.error) {
        return payload.error;
      }
    }
    return this.i18n.t('common.requestFailedApiJwt');
  }
}
